import { useEffect, useState } from 'react'
import { X, Check, AlertCircle } from 'lucide-react'
import { apiClient } from '../../services/api'
import { AIInsight } from '../../services/aiInsights'
import { toast } from 'sonner'

interface Props {
  onClose: () => void
}

export function OutcomePromptModal({ onClose }: Props) {
  const [pending, setPending] = useState<AIInsight[]>([])
  const [idx, setIdx] = useState(0)
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    apiClient
      .get<AIInsight[]>('/api/v1/ai/insights/pending-verification')
      .then(r => setPending(r.data))
      .catch(() => setPending([]))
      .finally(() => setLoading(false))
  }, [])

  const current = pending[idx]

  const submit = async (verified: boolean) => {
    if (!current) return
    try {
      await apiClient.post(`/api/v1/ai/insights/${current.id}/verify-outcome`, {
        outcome_verified: verified,
        outcome_notes: notes || null,
      })
      toast.success('Thanks — outcome captured')
      setNotes('')
      if (idx + 1 >= pending.length) onClose()
      else setIdx(i => i + 1)
    } catch {
      toast.error('Failed to save outcome')
    }
  }

  if (loading) return null
  if (pending.length === 0) return null

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold">Did this insight pan out?</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          {idx + 1} of {pending.length} pending verifications
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-3">
          <p className="text-sm font-medium mb-1">{current.title}</p>
          {current.reasoning && <p className="text-xs text-gray-600">{current.reasoning}</p>}
        </div>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 mb-3"
        />
        <div className="flex gap-2">
          <button
            onClick={() => submit(true)}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium flex items-center justify-center gap-2"
          >
            <Check className="w-4 h-4" /> Yes, accurate
          </button>
          <button
            onClick={() => submit(false)}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
          >
            No, off the mark
          </button>
        </div>
      </div>
    </div>
  )
}
